import { EVENTS } from "@configs/statsig/constants";
import {
  buildUserProperties,
  resetRegistrationFormFocusOncePerSession,
  waitForStatsigReadyEvent,
} from "@configs/statsig/utils";
import { PayloadAction } from "@reduxjs/toolkit";
import { StatsigUser } from "@statsig/client-core";
import logger from "@utils/logger";
import { ProfileResponse } from "kz-ui-sdk";
import { call, select, takeEvery } from "redux-saga/effects";
import { RootState } from "../index";
import { AuthSliceState, authSlice } from "./slices";

// Function to update Statsig user properties
async function updateStatsigUser(self: any, serverPrefix?: string) {
  // Get Statsig client from global reference (set by AppStatsigProvider)
  logger._console.log("[Statsig] updateStatsigUser: start");
  const statsigClient = await waitForStatsigReadyEvent();

  if (!statsigClient) {
    logger._console.warn("Statsig client not available for user update");
    return;
  }

  if (self?.member) {
    const userProperties: StatsigUser = buildUserProperties(self.member, serverPrefix || "");

    statsigClient
      .updateUserAsync(userProperties)
      .then(() => {
        logger._console.log("Statsig user properties updated successfully", userProperties);
      })
      .catch((error: any) => {
        logger._console.error("Failed to update Statsig user properties", error);
      });
  }
}

// Function to reset Statsig user context on logout
async function resetStatsigUser() {
  // Get Statsig client from global reference (set by AppStatsigProvider)
  const statsigClient = (window as any).__statsig_client__;

  if (!statsigClient) {
    logger._console.warn("Statsig client not available for user reset");
    return;
  }

  try {
    // Full session reset (recommended approach)
    await statsigClient.shutdown();
    logger._console.log("Statsig user context reset successfully");
  } catch (error) {
    logger._console.error("Failed to reset Statsig user context", error);
  }
}

// Saga to handle updateProfile action
function* handleUpdateProfile(action: PayloadAction<ProfileResponse | null>): Generator<any, void, any> {
  try {
    // Get the updated auth state
    const authState: AuthSliceState = yield select((state: RootState) => state.auth);
    const { serverInfo } = yield select((state: RootState) => state.preferences);

    // Update Statsig with the new user properties
    yield call(updateStatsigUser, authState.self, serverInfo?.prefix);
  } catch (error) {
    logger._console.error("Error updating Statsig user properties:", error);
  }
}

// Saga to handle initAuthState action
function* handleInitAuthState(action: PayloadAction<any>): Generator<any, void, any> {
  try {
    // Get the updated auth state
    const authState: AuthSliceState = yield select((state: RootState) => state.auth);
    const { serverInfo } = yield select((state: RootState) => state.preferences);
    // Update Statsig with the initial user properties
    yield call(updateStatsigUser, authState.self, serverInfo?.prefix);
  } catch (error) {
    logger._console.error("Error updating Statsig user properties:", error);
  }
}

// Saga to handle logout action
function* handleLogout(action: PayloadAction<void>): Generator<any, void, any> {
  try {
    // Log logout event before resetting user context
    const statsigClient = (window as any).__statsig_client__;
    if (statsigClient) {
      statsigClient.logEvent(EVENTS.LOGOUT);
    }

    // Reset Statsig user context to prevent future activity from being attributed to the previous user
    yield call(resetStatsigUser);

    logger._console.log("[Statsig] handleLogout: resetRegistrationFormFocusOncePerSession");
    // Clear once-per-session flags related to registration focus
    yield call(resetRegistrationFormFocusOncePerSession);
  } catch (error) {
    logger._console.error("Error resetting Statsig user context on logout:", error);

    // Log the error event
    const statsigClient = (window as any).__statsig_client__;
    if (statsigClient) {
      statsigClient.logEvent(EVENTS.API_ERROR, undefined, {
        errorType: "logout_failure",
        errorMessage: error instanceof Error ? error.message : "Unknown logout error",
        severity: "medium",
      });
    }
  }
}

// Main saga that watches for auth actions
export function* statsigSaga() {
  yield takeEvery(authSlice.actions.updateProfile.type, handleUpdateProfile);
  yield takeEvery(authSlice.actions.initAuthState.type, handleInitAuthState);
  yield takeEvery(authSlice.actions.logout.type, handleLogout);
}
